// RITA-1549 — Texte zum Drucken der Vertragserstellung, wie in der Skizze 3c (Bilder A–D) und den Festlegungen
// F1–F17 von Julia am 20.10.2026 freigegeben. Kalendertage stehen als TT.MM.JJJJ, Zeitpunkte wie in
// vertragsentwurfTexte.ts in der Ortszeit des Browsers.
// RITA-1552 — Texte des Rücklaufs nach der Skizze Rücklauf vom 21.10.2026 (Bilder A–G, Fragen F1–F10 von Julia „wie
// empfohlen“ freigegeben) und den Entscheidungen E1–E6 vom 22.10.2026: Knöpfe der Karte, Vertragsblock mit Fortschritt
// und nächstem Schritt, neue Protokollzeilen (Scan, Rücksendung), Fenster „Unterschriebenen Vertrag hochladen“ und
// „Rücksendung drucken“, die Rückfragen „Schon eine Rücksendung gedruckt“ und „Es fehlen Angaben“ sowie die
// Nutzertexte der neuen Fehlercodes. Ausnahme vom Browser-Ortszeit-Muster: Der Name des Scans (scanDateiname) nimmt
// wie der Server den Kalendertag in Berlin, weil der Satz „Wird unter „Dokumente“ gespeichert als …“ den gespeicherten
// Namen zeigt.
import type { ContractCreationKind } from 'global-intern-types';
import { PDF_PROBE_FENSTER_BLOCKIERT } from '../../templates/hooks/usePdfProbe';
import type { Druckeintrag } from '../api/vertragsdruck';
import {
  ART_KURZ,
  ART_TITEL,
  VERTRAGSINHABER_ART_EINTRAGEN,
  VERTRAGSINHABER_ART_SPERRE,
} from './vertragserstellungTexte';
import {
  entwurfsdatum,
  entwurfszeit,
  FENSTER_FUSS_DRUCK as FUSS_DRUCK,
  leerText,
  person,
  PLATZHALTER_FEHLER,
} from './vertragsentwurfTexte';
import { druckDateiname } from './scanDateiname';

/** Der Fußsatz des Fensters liegt in vertragsentwurfTexte.ts (kein Import-Zyklus); hier unter dem 3c-Namen. */
export { FENSTER_FUSS_DRUCK } from './vertragsentwurfTexte';

// Bild A – Fenster „Vertrag erstellen“: Häkchen, „Drucken“, Vorlage geändert
export const WIRD_NICHT_GEDRUCKT = 'wird nicht gedruckt';
export const DRUCKEN = 'Drucken';
export const WIRD_GEDRUCKT = 'Wird gedruckt …';
/** Häkchen gesperrt: Ohne Änderungsstand (`updated_at`) kann der Server die Vorlage nicht vergleichen. */
export const OHNE_STAND_HINWEIS =
  'Nicht druckbar: Diese Vorlage hat keinen Änderungsstand. Bitte die Vorlage unter Einstellungen › Vorlagen › Vertragsvorlagen einmal neu speichern.';
export const VORSCHAU_NEU_LADEN = 'Vorschau neu laden';
/** Hinweis b) der Skizze; bei gelöschter Vorlage (template_status „deleted“) mit eigenem Wortlaut. */
export const vorlageGeaendertText = (name: string, status: string) =>
  status === 'deleted'
    ? `Die Vorlage „${name}“ gibt es nicht mehr – Vorschau neu laden.`
    : `Die Vorlage „${name}“ wurde geändert – Vorschau neu laden.`;
export const DRUCKFEHLER = 'Die PDF konnte nicht erstellt werden. Bitte erneut versuchen.';
export const DRUCK_AUFBAU_FEHLER =
  'Die PDF konnte nicht erstellt werden, weil der Aufbau eines Dokuments ungültig ist.';
export const DRUCK_KEINE_AUSWAHL = 'Bitte mindestens ein Dokument zum Drucken auswählen.';
/** Vorprüfung und DOCUMENT_TOO_LARGE beim Drucken (gleiche Grenzen wie beim Speichern, eigener Wortlaut). */
export const DRUCK_ZU_GROSS = 'Die Dokumente sind zu groß zum Drucken. Bitte kürzen.';
export const TAB_BLOCKIERT = PDF_PROBE_FENSTER_BLOCKIERT;

// Bild B – Rückfrage „Schon ein Vertrag gedruckt“ (Entscheidung 5)
export const SCHON_GEDRUCKT_TITEL = 'Schon ein Vertrag gedruckt';
export const schonGedrucktText = (datumIso: string) =>
  `Für diesen Berater wurde am ${entwurfsdatum(datumIso)} schon ein Vertrag gedruckt. Trotzdem einen neuen erstellen?`;
export const SCHON_GEDRUCKT_HINWEIS =
  'Ein neuer Vertrag nimmt den heutigen Stand der Vorlage. Denselben Vertrag noch einmal: Aktionsprotokoll › Erneut drucken.';
export const TROTZDEM_NEU = 'Trotzdem neuen erstellen';

// Bild C – Bearbeiten-Seite: Drucken-Fenster (F4), Konflikt (F14), Hinweis nach misslungenem Dateiabruf
export const DRUCKEN_FENSTER_TITEL = 'Drucken';
export const DRUCKEN_FENSTER_TEXT =
  'Gedruckt wird der Stand, den du gerade siehst – auch nicht gespeicherte Änderungen. Die PDF öffnet sich in einem neuen Tab und liegt danach unter „Dokumente“. Der Entwurf ist damit erledigt.';
export const DRUCKEN_FENSTER_TEILAUSWAHL = 'Das nicht angehakte Dokument bleibt im Entwurf.';
/**
 * Ohne Vertrag heißt die Datei „Anschreiben …“ (Server, Entscheidung 2); null, sobald der Vertrag dabei ist.
 * RITA-1552: Die Rücksendung heißt immer „Rücksendung …“ – kein Hinweis (null), auch ohne Vertrag.
 * RITA-1600 (Fund 106, Wortlaut von Julia am 03.11.2026 freigegeben): mit dem echten Namen, wie der Server ihn bildet.
 */
export const dateinameHinweis = (
  kinds: readonly ContractCreationKind[],
  beraterName: string,
  drucktag: Date,
): string | null =>
  kinds.includes('contract') || !kinds.includes('dispatch_letter')
    ? null
    : `Die Datei heißt „${druckDateiname(beraterName, kinds, drucktag)}“, weil der Vertrag nicht dabei ist.`;
export const TROTZDEM_DRUCKEN = 'Trotzdem drucken';
export const DRUCK_KONFLIKT_TITEL = 'Entwurf wurde inzwischen gespeichert';
export const druckKonfliktText = (name: string | null, zeit: string) =>
  `Der Entwurf wurde inzwischen von ${person(name)} gespeichert (${entwurfszeit(zeit)}).`;
export const druckKonfliktHinweis = (name: string | null) =>
  `Gespeicherten Stand laden: Deine Änderungen gehen verloren. Trotzdem drucken: Gedruckt wird dein Stand; der Entwurf von ${person(
    name,
  )} gilt damit als erledigt.`;
export const DRUCK_VERWORFEN_TITEL = 'Entwurf wurde inzwischen verworfen';
export const DRUCK_VERWORFEN_TEXT = 'Jemand hat den Entwurf in der Zwischenzeit verworfen.';
export const DRUCK_VERWORFEN_HINWEIS =
  'Trotzdem drucken: Gedruckt wird dein Stand; einen Entwurf gibt es danach nicht.';
export const ABGELEGT_NICHT_GEOEFFNET =
  'Die PDF wurde unter „Dokumente“ abgelegt, konnte aber nicht geöffnet werden – Aktionsprotokoll › Ansehen.';
/** Nach einem Teil-Druck (F11: nicht alle enthaltenen Dokumente angehakt) bleibt die Seite offen – mit diesem Hinweis. */
export const TEIL_GEDRUCKT_HINWEIS = 'Gedruckt. Der Entwurf bleibt erhalten.';

// Bild D – Aktionsprotokoll in der Karte (Entscheidung 7, F6, F8, F9, F12). RITA-1552: ohne eigene Überschrift – die
// Skizze Rücklauf (Bilder A, B) zeigt die Vertragsblöcke mit ihrem Knopf „Aktionsprotokoll ausblenden ▴ / anzeigen ▾“.
export const PROTOKOLL_LADEFEHLER = 'Das Aktionsprotokoll konnte nicht geladen werden.';
export const VORLAGE_INZWISCHEN_GEAENDERT = '(Vorlage inzwischen geändert)';
export const DATEI_GELOESCHT = 'Datei gelöscht';
export const ANSEHEN = 'Ansehen';
export const ERNEUT_DRUCKEN = 'Erneut drucken';

// Fehler beim Drucken, Erneut drucken und Ansehen
export const DATEI_GELOESCHT_FEHLER = 'Die Datei wurde unter „Dokumente“ gelöscht.';
export const SUPERSEDED_FEHLER =
  'Dieser Vertrag wurde inzwischen ersetzt – nur der neueste Vertrag lässt sich erneut drucken.';
/** RITA-1552 (E6): Die Rücksendung gehört nicht mehr zum neuesten Vertrag (CONTRACT_CHANGED). */
export const VERTRAG_GEAENDERT_FEHLER = 'Inzwischen wurde ein neuer Vertrag gedruckt – Fenster neu öffnen.';

// RITA-1552 – Bild A: Knöpfe der Karte (Reihenfolge Hochladen · Rücksendung · Vertrag erstellen), Vertragsblock
export const KNOPF_HOCHLADEN = 'Unterschriebenen Vertrag hochladen';
export const KNOPF_RUECKSENDUNG = 'Rücksendung drucken';
/** Bild A e: Hinweis beim Darüberfahren über den grauen Knopf „Hochladen“, solange kein Vertrag im PRM gedruckt ist. */
export const HOCHLADEN_GESPERRT_TOOLTIP =
  'Erst möglich, wenn ein Vertrag im PRM gedruckt wurde. Ältere Verträge bitte wie bisher unter „Dokumente“ hochladen.';
export const CHIP_GEDRUCKT = 'Gedruckt';
export const CHIP_UNTERSCHRIEBEN = 'Unterschrieben zurück';
export const CHIP_RUECKSENDUNG = 'Rücksendung';
/** „✓ Gedruckt“ (erledigt) bzw. „○ Rücksendung“ (offen). */
export const chipText = (text: string, erreicht: boolean) => `${erreicht ? '✓' : '○'} ${text}`;
export const NAECHSTER_SCHRITT_HOCHLADEN = 'Nächster Schritt: Unterschriebenen Vertrag hochladen';
export const NAECHSTER_SCHRITT_RUECKSENDUNG = 'Nächster Schritt: Rücksendung drucken';
/** Bild A d (Entscheidung 2): ohne Scan abgeschlossen. */
export const OHNE_SCAN_ZUSATZ = '(ohne Scan)';
/** „✓ Abgeschlossen am 28.10.2026“, ohne Scan mit „(ohne Scan)“; Datum = erste Rücksendung des Vertrags (E2). */
export const abgeschlossenText = (datumIso: string, ohneScan: boolean) =>
  `✓ Abgeschlossen am ${entwurfsdatum(datumIso)}${ohneScan ? ` ${OHNE_SCAN_ZUSATZ}` : ''}`;
export const PROTOKOLL_AUSBLENDEN = 'Aktionsprotokoll ausblenden ▴';
export const PROTOKOLL_ANZEIGEN = 'Aktionsprotokoll anzeigen ▾';
// Bild B: ältere Verträge eingeklappt
/** „▸ 2 ältere Verträge · Einblenden“, bei einem „▸ 1 älterer Vertrag · Einblenden“. */
export const aeltereVertraegeText = (anzahl: number) =>
  `▸ ${anzahl} ${anzahl === 1 ? 'älterer Vertrag' : 'ältere Verträge'} · Einblenden`;
export const AELTERE_AUSBLENDEN = '▴ Ältere Verträge ausblenden';
/** Bild B/D b (E4): Block der Einträge ohne Vertrag aus dem PRM, ganz unten in der Karte. */
export const OHNE_VERTRAG_BLOCK = 'Ohne Vertrag aus dem PRM';
// Neue Protokollzeilen (Bild A, F10)
export const SCAN_OEFFNEN = 'Scan öffnen';
/** „⬆ Unterschriebener Vertrag hochgeladen (Scan) · Erika Beispiel“ */
export const scanHochgeladenText = (name: string | null) =>
  `⬆ Unterschriebener Vertrag hochgeladen (Scan) · ${person(name)}`;

/** Zeitwert eines ISO-Strings; NaN, wenn er fehlt oder unlesbar ist. */
const zeitwert = (iso: string | null | undefined): number => (iso ? new Date(iso).getTime() : NaN);

/** Ist `a` jünger als `b`? Bei gleichem (oder unlesbarem) Zeitpunkt entscheidet die höhere Kennung. */
export function neuerAls(a: Druckeintrag, b: Druckeintrag): boolean {
  const zeitA = zeitwert(a.created_at);
  const zeitB = zeitwert(b.created_at);
  if (!Number.isNaN(zeitA) && !Number.isNaN(zeitB) && zeitA !== zeitB) return zeitA > zeitB;
  return a.id > b.id;
}

/** „Stand vom“ = jüngerer Vorlagenstand der gedruckten Dokumente (F12), TT.MM.JJJJ; null ohne Stand. */
export function standVomDatum(eintrag: Druckeintrag): string | null {
  const staende = eintrag.documents
    .map((dokument) => dokument.template_updated_at)
    .filter((iso): iso is string => !Number.isNaN(zeitwert(iso)));
  if (staende.length === 0) return null;
  const juengster = staende.reduce((bisher, iso) => (zeitwert(iso) > zeitwert(bisher) ? iso : bisher));
  return entwurfsdatum(juengster);
}

/** „✓ Gedruckt (für den Postversand) · <Vorlage A> + <Vorlage V> · Stand vom <TT.MM.JJJJ> · <Name>“ (Entscheidung 7). */
export function gedrucktText(eintrag: Druckeintrag): string {
  const vorlagen = eintrag.documents.map(
    (dokument) => (dokument.template_name ?? '').trim() || ART_KURZ[dokument.kind],
  );
  const stand = standVomDatum(eintrag);
  const teile = [
    '✓ Gedruckt (für den Postversand)',
    vorlagen.join(' + '),
    stand ? `Stand vom ${stand}` : '',
    person(eintrag.created_by_name),
  ];
  return teile.filter((teil) => teil !== '').join(' · ');
}

export const erneutGedrucktText = (name: string | null) => `↻ Erneut gedruckt · ${person(name)}`;

/** RITA-1552 (Bild A c): „✉ Rücksendung gedruckt · <Vorlage> · Stand vom <TT.MM.JJJJ> · <Name>“. */
export function ruecksendungGedrucktText(eintrag: Druckeintrag): string {
  const vorlagen = eintrag.documents.map(
    (dokument) => (dokument.template_name ?? '').trim() || ART_TITEL[dokument.kind],
  );
  const stand = standVomDatum(eintrag);
  const teile = [
    '✉ Rücksendung gedruckt',
    vorlagen.join(' + '),
    stand ? `Stand vom ${stand}` : '',
    person(eintrag.created_by_name),
  ];
  return teile.filter((teil) => teil !== '').join(' · ');
}

/**
 * RITA-1552 (Bild A/B, Kritik P2): Titel eines Vertragsblocks „<Vertragsvorlage> · gedruckt am <TT.MM.JJJJ>“ – der
 * Name des Dokuments mit Art `contract` (ohne Namen „Vertrag“) und der Tag des Drucks. Ebenso „Gehört zu“ und die
 * Auswahl im Hochladen-Fenster.
 */
export function vertragsblockTitel(eintrag: Druckeintrag): string {
  const vertrag = eintrag.documents.find((dokument) => dokument.kind === 'contract');
  const name = (vertrag?.template_name ?? '').trim() || ART_TITEL.contract;
  return `${name} · gedruckt am ${entwurfsdatum(eintrag.created_at)}`;
}

/**
 * „Ersetzt durch Vertrag vom …“; `datumIso` = created_at des ersetzenden Eintrags. RITA-1552 (E5): gehört in den Kopf
 * des Vertragsblocks (Status statt „Nächster Schritt“), nicht zusätzlich als graue Unterzeile an der Druckzeile.
 */
export const ersetztText = (datumIso: string) => `Ersetzt durch Vertrag vom ${entwurfsdatum(datumIso)}`;

// RITA-1552 – Bild C: Fenster „Unterschriebenen Vertrag hochladen“
/** Größte Datei in Bytes (20 MB) – dieselbe Grenze wie im Server (PDF_UPLOAD_MAX_BYTES). */
export const SCAN_MAX_BYTES = 20 * 1024 * 1024;
export const HOCHLADEN_TITEL = KNOPF_HOCHLADEN;
export const HOCHLADEN_FRAGE = 'Zu welchem Vertrag gehört der Scan?';
export const HOCHLADEN_FELD = 'Scan (PDF, höchstens 20 MB)';
/** „Datei hierher ziehen oder auswählen“; „auswählen“ ist in der Skizze unterstrichen und steht deshalb für sich. */
export const HOCHLADEN_ZIEHEN = 'Datei hierher ziehen oder';
export const HOCHLADEN_AUSWAEHLEN = 'auswählen';
export const ENTFERNEN = 'entfernen';
/** Größe der gewählten Datei: „2,4 MB“; unter 1 MB in kB wie in der Liste „Dokumente“ („96 kB“). */
export function dateigroesseText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`;
}
/** „Scan_Muster_unterschrieben.pdf · 2,4 MB“ */
export const gewaehlteDateiText = (name: string, bytes: number) => `${name} · ${dateigroesseText(bytes)}`;
export const hochladenSpeichernAls = (dateiname: string) => `Wird unter „Dokumente“ gespeichert als „${dateiname}“.`;
export const HOCHLADEN = 'Hochladen';
export const WIRD_HOCHGELADEN = 'Wird hochgeladen …';
/** Bild C c: keine PDF (auch leer) bzw. zu groß – direkt unter dem Feld. */
export const KEINE_PDF = 'Bitte eine PDF-Datei wählen.';
export const ZU_GROSS_20MB = 'Die Datei ist größer als 20 MB.';
/** Bild C d: Hochladen schlägt fehl; das Fenster bleibt offen. */
export const HOCHLADEN_FEHLER = 'Der Scan konnte nicht hochgeladen werden. Bitte erneut versuchen.';
/** Bild C b: Für den gewählten Vertrag gibt es schon einen Scan; `datumIso` = created_at des neuesten Scans. */
export const zweiterScanHinweis = (datumIso: string) =>
  `Für diesen Vertrag wurde am ${entwurfsdatum(
    datumIso,
  )} schon ein Scan hochgeladen. Der neue kommt dazu, der erste bleibt unter „Dokumente“.`;
/** Zusatz einer Option der Auswahl (Bild C a); Vorrang: Scan vorhanden vor ersetzt vor noch kein Scan. */
export type ScanZusatz = 'ersetzt' | 'scanVorhanden' | 'keinScan';
export const ZUSATZ_KEIN_SCAN = 'noch kein Scan';
export const ZUSATZ_ERSETZT = 'ersetzt';
export const ZUSATZ_SCAN = 'Scan vorhanden';
const ZUSATZ_TEXT: Record<ScanZusatz, string> = {
  ersetzt: ZUSATZ_ERSETZT,
  scanVorhanden: ZUSATZ_SCAN,
  keinScan: ZUSATZ_KEIN_SCAN,
};
/**
 * Eine Option der Auswahl als Teile: „Kooperationsvertrag MUGL weiblich · gedruckt am 21.09.2027“ und „– noch kein
 * Scan“. Das Fenster setzt den Zusatz grau hinter den Vertrag (Abschlussreview MR 2, S1: Anzeige und Test hängen am
 * selben Helfer).
 */
export const vertragAuswahlTeile = (eintrag: Druckeintrag, zusatz: ScanZusatz) => ({
  vertrag: vertragsblockTitel(eintrag),
  zusatz: `– ${ZUSATZ_TEXT[zusatz]}`,
});

export { scanDateiname, SCAN_DATEINAME_ZUSATZ } from './scanDateiname';

/** Nutzertext je Fehlercode beim Hochladen des Scans (Server: readPdfUpload, contractCreationScanUpload). */
export function scanFehlertextFuerCode(code: string | undefined): string {
  switch (code) {
    case 'EMPTY_FILE':
    case 'NOT_PDF':
      return KEINE_PDF;
    case 'FILE_TOO_LARGE':
      return ZU_GROSS_20MB;
    default:
      return HOCHLADEN_FEHLER;
  }
}

/**
 * Nutzertext, wenn das Dateifeld (react-dropzone) eine Datei ablehnt: zu groß → ZU_GROSS_20MB; falscher Typ, mehrere
 * Dateien oder Unbekanntes → KEINE_PDF (Kritik zum Bauplan: Fehler kommen aus fileRejections, nicht aus acceptedFiles).
 */
export const dateiablehnungText = (code: string | undefined) => (code === 'file-too-large' ? ZU_GROSS_20MB : KEINE_PDF);

// RITA-1552 – Bild D: Fenster „Rücksendung drucken“
export const RUECKSENDUNG_FENSTER_TITEL = KNOPF_RUECKSENDUNG;
const GEHOERT_ZU = 'Gehört zu:';
/**
 * „Gehört zu: <Vertrag> · gedruckt am …“ als Teile; das Fenster setzt den Vertrag fett (Abschlussreview MR 2, S1:
 * Anzeige und Test hängen am selben Helfer).
 */
export const gehoertZuTeile = (vertrag: Druckeintrag) => ({ vor: GEHOERT_ZU, vertrag: vertragsblockTitel(vertrag) });
/** Bild D b (Fall B): kein Vertrag im PRM gedruckt. */
export const GEHOERT_ZU_KEINEM =
  'Gehört zu: keinem im PRM gedruckten Vertrag. Die Rücksendung steht im Aktionsprotokoll für sich.';
export const KEIN_SCAN_HINWEIS =
  'Für diesen Vertrag ist noch kein unterschriebener Scan hochgeladen. Du kannst die Rücksendung trotzdem drucken.';
export const RUECKSENDUNG_FUSS = `${FUSS_DRUCK} Den gegengezeichneten Vertrag legst du als Papier bei.`;

// Bild D a: Rückfrage „Schon eine Rücksendung gedruckt“ (nur mit Vertrag aus dem PRM, E3)
export const SCHON_RUECKSENDUNG_TITEL = 'Schon eine Rücksendung gedruckt';
export const schonRuecksendungText = (vertragIso: string, ruecksendungIso: string) =>
  `Für den Vertrag vom ${entwurfsdatum(vertragIso)} wurde am ${entwurfsdatum(
    ruecksendungIso,
  )} schon eine Rücksendung gedruckt. Trotzdem eine neue erstellen?`;
export const SCHON_RUECKSENDUNG_HINWEIS = 'Dieselbe Rücksendung noch einmal: Aktionsprotokoll › Erneut drucken.';
export const TROTZDEM_NEUE = 'Trotzdem neue erstellen';

// RITA-1552 – Bild F: Rückfrage „Es fehlen Angaben“ vor jedem Druck (Entscheidung 4)
export const FEHLEND_TITEL = 'Es fehlen Angaben';
export const FEHLEND_TEXT = 'Diese Angaben fehlen beim Berater. Die Stellen bleiben im Druck leer:';
export const FEHLEND_FRAGE = 'Trotzdem drucken?';
export const FEHLEND_HINWEIS =
  'Abbrechen: Nichts wird gedruckt – Angaben beim Berater eintragen, speichern und das Fenster neu öffnen. Trotzdem drucken: Die PDF wird mit leeren Stellen erstellt.';
// RITA-1599 – Rückfrage im Fenster „Vertrag erstellen“, wenn für Vertrag oder Anschreiben keine Vorlage passt (Wortlaut
// mit Julia am 03.11.2026 abgestimmt); zusammen mit fehlenden Angaben eine einzige Rückfrage „Bitte vor dem Druck prüfen“.
export const VERTRAG_FEHLT_TITEL = 'Der Vertrag fehlt';
export const VERTRAG_FEHLT_TEXT =
  'Für diesen Berater passt keine Vertragsvorlage. Gedruckt wird nur das Anschreiben. Das Anschreiben kündigt aber einen Vertrag an.';
export const VERTRAG_FEHLT_HINWEIS =
  'Abbrechen: Nichts wird gedruckt. Zuerst die passende Vertragsvorlage anlegen und das Fenster neu öffnen. Trotzdem drucken: Nur das Anschreiben wird gedruckt, die Datei heißt „Anschreiben Vorname Nachname Datum.pdf“.';
export const ANSCHREIBEN_FEHLT_TITEL = 'Das Anschreiben fehlt';
export const ANSCHREIBEN_FEHLT_TEXT =
  'Für diesen Berater passt kein Anschreiben Versand. Gedruckt wird nur der Vertrag, ohne Anschreiben.';
export const ANSCHREIBEN_FEHLT_HINWEIS =
  'Abbrechen: Nichts wird gedruckt. Zuerst das passende Anschreiben Versand anlegen und das Fenster neu öffnen. Trotzdem drucken: Nur der Vertrag wird gedruckt.';
export const VOR_DEM_DRUCK_PRUEFEN_TITEL = 'Bitte vor dem Druck prüfen';
/** Titel, Satz und grauer Hinweis je fehlendem Dokument (`contract` bzw. `dispatch_letter`). */
export const FEHLENDES_DOKUMENT = {
  contract: { titel: VERTRAG_FEHLT_TITEL, text: VERTRAG_FEHLT_TEXT, hinweis: VERTRAG_FEHLT_HINWEIS },
  dispatch_letter: { titel: ANSCHREIBEN_FEHLT_TITEL, text: ANSCHREIBEN_FEHLT_TEXT, hinweis: ANSCHREIBEN_FEHLT_HINWEIS },
} as const;

export const FEHLEND_SEITE_TITEL = 'Beim Anlegen fehlten Angaben';
/** Bild F b: „Beim Anlegen des Entwurfs waren diese Angaben leer: Fax, IBAN. …“ */
export const fehlendSeiteText = (angaben: string[]) =>
  `Beim Anlegen des Entwurfs waren diese Angaben leer: ${angaben.join(
    ', ',
  )}. Falls du die Stellen nicht im Text ergänzt hast, bleiben sie im Druck leer. Trotzdem drucken?`;
export const FEHLEND_SEITE_HINWEIS =
  'Abbrechen: Nichts wird gedruckt – die Stellen im Text ergänzen oder nach dem Eintragen beim Berater „Neu beginnen“. Trotzdem drucken: Gedruckt wird der Stand, den du siehst.';

/**
 * Nutzertext je Fehlercode des Servers. Vorprüfungstexte wie beim Speichern (INVALID_CONTENT mit `kind`,
 * INVALID_PLACEHOLDER, DOCUMENT_TOO_LARGE); PAGE_SETTINGS_DIFFER reicht den Servertext durch; FILE_DELETED,
 * SUPERSEDED und (RITA-1552, E6) CONTRACT_CHANGED haben eigene Texte; alles andere (NOT_FOUND, STORAGE_FAILED,
 * PDF_FAILED, NO_USER, …) ist DRUCKFEHLER. Das Hochladen des Scans hat eigene Texte (scanFehlertextFuerCode).
 * TEMPLATE_CHANGED, CONFLICT und DISCARDED werden vom Hook gesondert behandelt und landen hier nur als Rückfall.
 */
export function fehlertextFuerCode(
  code: string | undefined,
  servertext?: string | null,
  kind?: ContractCreationKind | null,
): string {
  switch (code) {
    case 'INVALID_CONTENT':
      // RITA-1552: der leere Teil je Art, auch das Anschreiben Rücksendung; ohne Art wie bisher der Vertrag.
      return leerText(kind ?? 'contract');
    case 'INVALID_PLACEHOLDER':
      return PLATZHALTER_FEHLER;
    case 'DOCUMENT_TOO_LARGE':
      return DRUCK_ZU_GROSS;
    case 'INVALID_PRINT':
    case 'INVALID_DOCUMENT':
      return DRUCK_AUFBAU_FEHLER;
    case 'PAGE_SETTINGS_DIFFER':
      return (servertext ?? '').trim() || DRUCKFEHLER;
    case 'FILE_DELETED':
      return DATEI_GELOESCHT_FEHLER;
    case 'SUPERSEDED':
      return SUPERSEDED_FEHLER;
    case 'CONTRACT_CHANGED':
      return VERTRAG_GEAENDERT_FEHLER;
    case 'BUSINESS_OWNER_TYPE_MISSING':
      return `${VERTRAGSINHABER_ART_EINTRAGEN} ${VERTRAGSINHABER_ART_SPERRE}`;
    default:
      return DRUCKFEHLER;
  }
}
